import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaMagic, FaGlobe, FaUndo, FaComments, FaPaperPlane } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import Field, { TextArea, TextInput } from '../../../components/ui/Field';
import AgentDot from '../../../components/ui/AgentDot';
import { engineService } from '../../../services/engineService';
import { scan } from '../../../utils/templatePreview';
import { applyHunks, diffLines, hunksOf, toSegments, wordDiff } from '../../../utils/promptDiff';

const MODE_LABEL = { chat: 'Conversar con el asistente', mejorar: 'Mejorar la redacción', web: 'Agregar información de una página' };
const PROPOSAL_TITLE = { chat: 'Propuesta del asistente', mejorar: 'Propuesta: mejorar la redacción', web: 'Propuesta: información de la página' };
const WORKING = { mejorar: 'Revisando la redacción…', web: 'Leyendo la página y redactando…' };
const QUESTIONS = ['¿Mi prompt menciona alguna herramienta que no exista o no esté conectada?', 'Agrega la herramienta que registra leads donde corresponda', 'Haz que hable de usted', '¿Qué datos pide cada herramienta antes de usarla?'];

const money = (n) => (n == null ? null : n > 0 && n < 0.01 ? 'menos de 1 centavo de dólar' : `$${n.toFixed(2)}`);

/** Una línea del diff con las palabras que cambian resaltadas. */
/** Pone en rojo, dentro de un texto, las variables {{…}} que el motor no entiende (con su motivo al pasar el cursor). */
function redVars(text, explain) {
  if (!explain || typeof text !== 'string' || !text.includes('{{')) return text;
  const out = []; let last = 0;
  scan(text).forEach((t, i) => {
    const why = explain(t.expr); if (!why) return;
    out.push(text.slice(last, t.start), <mark key={i} title={why} className="rounded bg-signal-soft px-0.5 text-signal ring-1 ring-signal/50 underline decoration-wavy decoration-signal underline-offset-2">{text.slice(t.start, t.end)}</mark>);
    last = t.end;
  });
  return out.length ? [...out, text.slice(last)] : text;
}

function Line({ kind, parts, text, explain }) {
  const del = kind === 'del';
  return (
    <div className={`flex gap-2 px-3 py-1 text-[13.5px] leading-relaxed ${del ? 'bg-signal-soft' : 'bg-live-soft'}`}>
      <span aria-hidden className={`select-none w-3 shrink-0 font-semibold ${del ? 'text-signal' : 'text-live'}`}>{del ? '−' : '+'}</span>
      <span className="sr-only">{del ? 'Quitar: ' : 'Poner: '}</span>
      <span className="min-w-0 whitespace-pre-wrap break-words text-fg">
        {parts ? parts.map((p, i) => (p.hl ? <mark key={i} className={`rounded px-0.5 text-fg ${del ? 'bg-signal/30' : 'bg-live/30'}`}>{p.t}</mark> : <React.Fragment key={i}>{del ? p.t : redVars(p.t, explain)}</React.Fragment>))
          : (text === '' ? <span className="text-fg-faint italic">(línea en blanco)</span> : (del ? text : redVars(text, explain)))}
      </span>
    </div>
  );
}

function Context({ lines, head, tail }) {
  if (lines.length <= 5 || (!head && !tail)) {
    const show = !head && !tail ? [] : lines;
    return show.length ? <div className="px-3 py-1 text-[13px] text-fg-faint whitespace-pre-wrap">{show.join('\n')}</div> : null;
  }
  const top = head ? lines.slice(0, 2) : [];
  const bottom = tail ? lines.slice(-2) : [];
  const hidden = lines.length - top.length - bottom.length;
  return (
    <>
      {top.length > 0 && <div className="px-3 py-1 text-[13px] text-fg-faint whitespace-pre-wrap">{top.join('\n')}</div>}
      {hidden > 0 && <div className="px-3 py-1 text-[12px] text-fg-faint text-center">· {hidden} {hidden === 1 ? 'línea' : 'líneas'} sin cambios ·</div>}
      {bottom.length > 0 && <div className="px-3 py-1 text-[13px] text-fg-faint whitespace-pre-wrap">{bottom.join('\n')}</div>}
    </>
  );
}

/**
 * Asistente de redacción del prompt. Mejora la redacción, hace un cambio que se le pide o agrega información de una página web, pero
 * SIEMPRE como propuesta: se ven los cambios línea por línea y la persona elige cuáles aplicar. No escribe nada por su cuenta.
 */
export default function PromptAssistant({ explain, agent, text, readOnly, engineReady, hasCredential, onApply, onGoCredential }) {
  const [panel, setPanel] = useState(null);          // null | 'chat' | 'web'
  // Conversación: vive solo en esta pantalla (nada se guarda; al salir se borra). Notas = avisos de la pantalla, no se mandan al modelo.
  const [chat, setChat] = useState([]);              // [{ role: 'user' | 'assistant' | 'note', content }]
  const [draftMsg, setDraftMsg] = useState('');
  const listRef = useRef(null);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null);    // { data, base, mode, accepted:Set }
  const [applied, setApplied] = useState(null);      // { undoText, count }
  const abort = useRef(null);

  useEffect(() => () => abort.current?.abort(), []);

  const segs = useMemo(() => (proposal ? toSegments(diffLines(proposal.base, proposal.data.prompt)) : []), [proposal]);
  const hunks = useMemo(() => hunksOf(segs), [segs]);
  const stale = proposal && text !== proposal.base;

  const run = async (mode) => {
    setError(null); setApplied(null); setProposal(null);
    if (mode === 'mejorar' && !text.trim()) { setError({ message: 'Escribe primero las instrucciones del agente: no hay nada que mejorar.' }); return; }
    const ctl = new AbortController(); abort.current = ctl; setBusy(mode);
    try {
      const data = await engineService.promptAssist(agent.id, { mode, prompt: text, instruction: mode === 'web' ? note.trim() : '', url: mode === 'web' ? url.trim() : undefined }, { signal: ctl.signal });
      const ops = toSegments(diffLines(text, data.prompt));
      setProposal({ data, base: text, mode, accepted: new Set(hunksOf(ops).map((h) => h.id)) });
    } catch (e) {
      if (e.code !== 'aborted') setError({ message: e.message, code: e.code });
    } finally { setBusy(''); abort.current = null; }
  };

  // Un turno de la conversación: manda el hilo, el prompt actual y la propuesta pendiente (si sigue vigente) y muestra la respuesta.
  const send = async (raw) => {
    const msg = (raw ?? draftMsg).trim();
    if (!msg || busy) return;
    setError(null); setApplied(null);
    const history = chat.filter((m) => m.role !== 'note').map(({ role, content }) => ({ role, content })).slice(-16);
    const pending = proposal && proposal.base === text ? proposal.data.prompt : undefined;
    const ctl = new AbortController(); abort.current = ctl; setBusy('chat');
    setChat((c) => [...c, { role: 'user', content: msg }]); setDraftMsg('');
    try {
      const data = await engineService.promptAssist(agent.id, { mode: 'chat', prompt: text, instruction: msg, history, pending }, { signal: ctl.signal });
      setChat((c) => [...c, { role: 'assistant', content: data.answer }]);
      if (data.proposed) setProposal({ data, base: text, mode: 'chat', accepted: new Set(hunksOf(toSegments(diffLines(text, data.prompt))).map((h) => h.id)) });
    } catch (e) {
      if (e.code !== 'aborted') setError({ message: e.message, code: e.code });
    } finally { setBusy(''); abort.current = null; }
  };
  useEffect(() => { listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight }); }, [chat, busy]);

  const toggle = (id) => setProposal((p) => { const a = new Set(p.accepted); if (a.has(id)) a.delete(id); else a.add(id); return { ...p, accepted: a }; });
  const setAll = (on) => setProposal((p) => ({ ...p, accepted: new Set(on ? hunks.map((h) => h.id) : []) }));
  const apply = () => {
    const final = applyHunks(segs, proposal.accepted);
    setApplied({ undoText: proposal.base, count: proposal.accepted.size });
    onApply(final);
    if (chat.length) setChat((c) => [...c, { role: 'note', content: proposal.accepted.size === 1 ? 'Aplicaste 1 cambio al borrador.' : `Aplicaste ${proposal.accepted.size} cambios al borrador.` }]);
    setProposal(null);
  };
  const undo = () => { onApply(applied.undoText); setApplied(null); };

  const disabled = readOnly || !engineReady || Boolean(busy);
  const blocked = !engineReady ? 'Falta configurar la dirección del motor (REACT_APP_ENGINE_URL) en el admin.' : !hasCredential ? 'El asistente usa la clave de OpenAI de este agente. Agrégala para poder usarlo.' : '';
  const toolBtn = (mode, icon, onClick) => (
    <Btn key={mode} variant={panel === mode ? 'primary' : 'quiet'} size="sm" icon={icon} onClick={onClick} disabled={disabled || Boolean(blocked)} aria-pressed={panel === mode}>{MODE_LABEL[mode]}</Btn>
  );

  return (
    <section aria-label="Asistente de redacción" className="rounded-3xl bg-panel ring-1 ring-line p-5 sm:p-6" style={{ boxShadow: 'var(--shadow)' }}>
      <div className="flex items-start gap-3.5">
        <AgentDot name="Asistente" palette="aurora" size={40} state={busy ? 'thinking' : proposal ? 'happy' : 'idle'} />
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[18px] font-bold text-fg">Asistente de redacción</h3>
          <p className="text-[13px] text-fg-dim mt-0.5 max-w-[64ch]">Conoce tu agente (herramientas conectadas, datos previos, variables y documentos). Conversa contigo para cambiar el prompt paso a paso, o mejora su redacción de una vez. Siempre te muestra una propuesta: tú eliges qué cambios aplicar.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {toolBtn('chat', FaComments, () => setPanel(panel === 'chat' ? null : 'chat'))}
        {toolBtn('mejorar', FaMagic, () => { setPanel(null); run('mejorar'); })}
        {toolBtn('web', FaGlobe, () => setPanel(panel === 'web' ? null : 'web'))}
      </div>
      {blocked && (
        <p role="note" className="mt-3 text-[13px] text-fg-faint">
          {blocked} {!hasCredential && engineReady && <button type="button" onClick={onGoCredential} className="text-live font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70 rounded">Ir a la clave</button>}
        </p>
      )}

      {panel === 'chat' && !blocked && (
        <div className="mt-4 rounded-2xl ring-1 ring-line bg-ink/40">
          <div ref={listRef} className="max-h-[380px] overflow-y-auto p-3 space-y-2.5" role="log" aria-live="polite" aria-label="Conversación con el asistente">
            {!chat.length && (
              <div className="py-2 text-[13.5px] text-fg-dim">
                <p>Platica con el asistente mientras trabajas el prompt: pídele cambios, que agregue una herramienta, o pregúntale cualquier duda. Recuerda lo que van diciendo; cada cambio llega como propuesta y tú decides qué aplicar.</p>
                <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Ideas para empezar">
                  {QUESTIONS.map((q) => <button key={q} type="button" onClick={() => send(q)} disabled={disabled} className="rounded-full px-3 py-1 text-left text-[12.5px] text-fg-dim ring-1 ring-line hover:text-fg hover:ring-fg/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">{q}</button>)}
                </div>
              </div>
            )}
            {chat.map((m, i) => (m.role === 'note'
              ? <p key={i} className="text-center text-[12px] text-live">{m.content}</p>
              : (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <p className={`max-w-[88%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed text-fg ${m.role === 'user' ? 'bg-live-soft' : 'bg-raised'}`}>
                    <span className="sr-only">{m.role === 'user' ? 'Tú: ' : 'Asistente: '}</span>{m.content}
                  </p>
                </div>
              )))}
            {busy === 'chat' && <p role="status" className="text-[12.5px] text-fg-faint">El asistente está pensando…</p>}
          </div>
          <form className="flex items-end gap-2 border-t border-line p-2.5" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <TextArea rows={2} value={draftMsg} onChange={(e) => setDraftMsg(e.target.value)} maxLength={1500} disabled={disabled} aria-label="Tu mensaje al asistente" placeholder="Escribe tu mensaje…"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }} />
            {busy === 'chat'
              ? <Btn type="button" variant="ghost" onClick={() => abort.current?.abort()}>Cancelar</Btn>
              : <Btn type="submit" variant="primary" icon={FaPaperPlane} disabled={disabled || !draftMsg.trim()}>Enviar</Btn>}
          </form>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 pb-2.5 text-[12px] text-fg-faint">
            <span>Enter envía, Shift + Enter hace salto de línea. La conversación no se guarda: se borra al salir de esta pantalla.</span>
            {chat.length > 0 && <button type="button" onClick={() => { setChat([]); setProposal(null); setError(null); }} disabled={Boolean(busy)} className="font-semibold text-fg-dim underline rounded hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">Nueva conversación</button>}
          </div>
        </div>
      )}
      {panel === 'web' && !blocked && (
        <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); run('web'); }}>
          <Field label="Dirección de la página" hint="Se lee solo esa página (no todo el sitio). Si es un PDF o la página se arma con JavaScript y no se puede leer, copia el texto y úsalo en «Pedir un cambio».">
            <TextInput type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.cliente.mx/servicios" maxLength={2000} spellCheck={false} disabled={disabled} />
          </Field>
          <Field label="Qué te interesa de esa página" optional hint="Por ejemplo: «Solo los precios y horarios». Si lo dejas vacío se toma lo útil para atender clientes.">
            <TextInput value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} disabled={disabled} />
          </Field>
          <Btn type="submit" variant="primary" disabled={disabled || !url.trim()} loading={busy === 'web'}>Leer la página y proponer</Btn>
        </form>
      )}

      {busy && busy !== 'chat' && (
        <div role="status" className="mt-4 flex flex-wrap items-center gap-3 text-[13.5px] text-fg-dim">
          <span>{WORKING[busy]} Puede tardar hasta un minuto.</span>
          <Btn size="sm" variant="ghost" onClick={() => abort.current?.abort()}>Cancelar</Btn>
        </div>
      )}
      {error && (
        <div role="alert" className="mt-4 rounded-2xl bg-signal-soft ring-1 ring-signal/30 px-4 py-3 text-[13.5px] text-fg">
          <p>{error.message}</p>
          {error.code === 'no_credential' && <Btn size="sm" className="mt-2" onClick={onGoCredential}>Ir a la clave de OpenAI</Btn>}
        </div>
      )}
      {applied && !proposal && (
        <p role="status" className="mt-4 flex flex-wrap items-center gap-3 text-[13.5px] text-live">
          Se {applied.count === 1 ? 'aplicó 1 cambio' : `aplicaron ${applied.count} cambios`} al borrador. Recuerda publicar para que el agente los use.
          <Btn size="sm" variant="ghost" icon={FaUndo} onClick={undo}>Deshacer</Btn>
        </p>
      )}

      {proposal && (
        <div className="mt-5 border-t border-line pt-5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h4 className="font-display text-[17px] font-bold text-fg">{PROPOSAL_TITLE[proposal.mode]}</h4>
            <p className="text-[12.5px] text-fg-faint">
              {proposal.data.usage.model} · {(proposal.data.usage.prompt + proposal.data.usage.completion).toLocaleString('es-MX')} tokens{money(proposal.data.usage.cost_usd) ? ` · ${money(proposal.data.usage.cost_usd)}` : ''}
            </p>
          </div>
          {proposal.data.source && (
            <p className="mt-1 text-[12.5px] text-fg-faint break-words">
              Fuente: <a href={proposal.data.source.url} target="_blank" rel="noopener noreferrer" className="text-fg-dim underline underline-offset-2 hover:text-fg">{proposal.data.source.title || proposal.data.source.url}</a>
              {' '}· se leyeron {proposal.data.source.chars.toLocaleString('es-MX')} caracteres{proposal.data.source.truncated ? ' (la página era más larga)' : ''}
            </p>
          )}

          {proposal.data.warnings.length > 0 && (
            <div role="note" className="mt-4 rounded-2xl bg-caution-soft ring-1 ring-caution/30 px-4 py-3">
              <p className="text-[13px] font-semibold text-fg">Revisa esto antes de aplicar</p>
              <ul className="mt-1 list-disc pl-4 space-y-1 text-[13px] text-fg-dim marker:text-caution">{proposal.data.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          {proposal.data.changes.length > 0 && (
            <ol className="mt-4 space-y-1.5 text-[13.5px]" aria-label="Resumen de cambios">
              {proposal.data.changes.map((c, i) => <li key={i} className="text-fg-dim"><span className="font-semibold text-fg">{c.title}.</span> {c.detail}</li>)}
            </ol>
          )}

          {hunks.length === 0 ? (
            <p className="mt-4 text-[13.5px] text-fg-dim">El asistente no propone cambios: el texto ya está bien así.</p>
          ) : (<>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <p className="text-[13.5px] font-semibold text-fg">{hunks.length === 1 ? '1 cambio propuesto' : `${hunks.length} cambios propuestos`}</p>
              <Btn size="sm" variant="ghost" onClick={() => setAll(true)}>Elegir todos</Btn>
              <Btn size="sm" variant="ghost" onClick={() => setAll(false)}>Quitar todos</Btn>
            </div>
            <div className="mt-2 rounded-2xl ring-1 ring-line overflow-hidden divide-y divide-line bg-ink/40">
              {segs.map((s, i) => (s.kind === 'eq'
                ? <Context key={i} lines={s.lines} head={segs[i - 1]?.kind === 'hunk'} tail={segs[i + 1]?.kind === 'hunk'} />
                : (
                  <div key={i} className="bg-panel/60">
                    <label className="flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium text-fg cursor-pointer border-b border-line">
                      <input type="checkbox" checked={proposal.accepted.has(s.id)} onChange={() => toggle(s.id)} className="h-4 w-4 accent-[rgb(var(--live))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70" />
                      Aplicar este cambio
                    </label>
                    {(() => {
                      const pair = s.del.length === s.add.length && s.del.length > 0;
                      const pairs = pair ? s.del.map((d, k) => wordDiff(d, s.add[k])) : null;
                      return (<>
                        {s.del.map((l, k) => <Line key={`d${k}`} kind="del" text={l} parts={pairs?.[k].del} />)}
                        {s.add.map((l, k) => <Line key={`a${k}`} kind="add" text={l} parts={pairs?.[k].add} explain={explain} />)}
                      </>);
                    })()}
                  </div>
                )))}
            </div>
          </>)}

          {stale && <p role="alert" className="mt-3 text-[13px] text-signal">Cambiaste el texto mientras el asistente trabajaba. Genera la propuesta otra vez para no pisar lo que escribiste.</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Btn variant="primary" onClick={apply} disabled={stale || proposal.accepted.size === 0 || hunks.length === 0}>
              {proposal.accepted.size === 0 ? 'Elige al menos un cambio' : `Aplicar ${proposal.accepted.size === 1 ? '1 cambio' : `${proposal.accepted.size} cambios`}`}
            </Btn>
            <Btn variant="ghost" onClick={() => setProposal(null)}>Descartar la propuesta</Btn>
            <span className="text-[12.5px] text-fg-faint">Se aplica al borrador; no cambia lo que responde el agente hasta que publiques.</span>
          </div>
        </div>
      )}
    </section>
  );
}
